import { usePageTitle } from "@/hooks/use-page-title";
import { useEffect, useState, useMemo } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import type { CarrierWithRelations, Company } from "@shared/schema";
import { useInfiniteQuery, useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { isUnauthorizedError } from "@/lib/authUtils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import CreateCarrierModal from "@/components/modals/create-carrier-modal";
import EditCarrierModal from "@/components/modals/edit-carrier-modal";
import NotesDialog from "@/components/notes/notes-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Plus, Search, Truck, Edit, Eye, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, Loader2 } from "lucide-react";
import { useLocation } from "wouter";

export default function Carriers() {
  usePageTitle("Carriers");
  const { toast } = useToast();
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 250);
  const [nameSort, setNameSort] = useState<"asc" | "desc" | "none">("none");

  const cycleNameSort = () =>
    setNameSort((s) => (s === "none" ? "asc" : s === "asc" ? "desc" : "none"));

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);

  const {
    data: carriersInfinite,
    isLoading: carriersLoading,
    fetchNextPage: carriersFetchNextPage,
    hasNextPage: carriersHasNextPage,
    isFetchingNextPage: carriersIsFetchingNextPage,
  } = useInfiniteQuery<{ data: CarrierWithRelations[]; nextCursor: string | null; totalCount?: number }>({
    queryKey: ["/api/carriers", { search: debouncedSearch }],
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (debouncedSearch.trim()) qs.set('search', debouncedSearch.trim());
      if (pageParam) qs.set('cursor', pageParam as string);
      const res = await fetch(`/api/carriers${qs.toString() ? `?${qs.toString()}` : ''}`, { credentials: 'include' });
      return res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnMount: "always",
  });

  const carriersFlat = useMemo(() =>
    carriersInfinite?.pages.flatMap(p => p.data) ?? [],
    [carriersInfinite]
  );
  const carriersTotalCount = carriersInfinite?.pages[0]?.totalCount ?? carriersFlat.length;

  const { data: companies } = useQuery<Company[]>({
    queryKey: ["/api/companies"],
    retry: false,
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  // Check for carrier ID in URL query params and navigate to details page
  useEffect(() => {
    if (!carriersLoading && carriersInfinite) {
      const params = new URLSearchParams(window.location.search);
      const carrierId = params.get('id');
      
      if (carrierId) {
        setLocation(`/carriers/${carrierId}`);
      }
    }
  }, [carriersFlat, carriersLoading, setLocation]);

  const createCarrierMutation = useMutation({
    mutationFn: async (carrierData: any) => {
      await apiRequest("POST", "/api/carriers", carrierData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
      toast({
        title: "Success",
        description: "Carrier created successfully",
      });
      setShowCreateModal(false);
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: "Failed to create carrier",
        variant: "destructive",
      });
    },
  });

  const updateCarrierMutation = useMutation({
    mutationFn: async (carrierData: any) => {
      await apiRequest("PUT", `/api/carriers/${selectedCarrier.id}`, carrierData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
      toast({
        title: "Success",
        description: "Carrier updated successfully",
      });
      setShowEditModal(false);
      setSelectedCarrier(null);
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: "Failed to update carrier",
        variant: "destructive",
      });
    },
  });

  if (isLoading || !isAuthenticated) {
    return <div className="min-h-screen bg-slate-50" />;
  }

  const handleCreateCarrier = (carrierData: any) => {
    createCarrierMutation.mutate(carrierData);
  };

  const handleUpdateCarrier = (carrierData: any) => {
    updateCarrierMutation.mutate(carrierData);
  };

  const handleEditCarrier = (carrier: any) => {
    setSelectedCarrier(carrier);
    setShowEditModal(true);
  };

  const handleViewCarrier = (carrier: any) => {
    setLocation(`/carriers/${carrier.id}`);
  };

  const handleViewProfile = (mcNumber: string) => {
    if (mcNumber && mcNumber !== "Not set") {
      let cleanMcNumber = mcNumber;
      if (cleanMcNumber.startsWith("MC-")) {
        cleanMcNumber = cleanMcNumber.substring(3); // Remove "MC-"
      } else if (cleanMcNumber.startsWith("MC")) {
        cleanMcNumber = cleanMcNumber.substring(2); // Remove "MC"
      }
      const profileUrl = `https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=MC_MX&query_string=${cleanMcNumber}`;
      window.open(profileUrl, "_blank");
    }
  };

  const filteredCarriers = [...carriersFlat].sort((a: any, b: any) => {
    if (nameSort === "none") return 0;
    return nameSort === "asc"
      ? (a.name ?? "").localeCompare(b.name ?? "")
      : (b.name ?? "").localeCompare(a.name ?? "");
  });

  return (
    <ResponsiveLayout title="Carriers" subtitle="Manage carrier information and FMCSA data" currentPage="carriers">
      <div className="space-y-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Carriers</h2>
            <p className="text-sm text-gray-600">Manage carrier information and FMCSA data</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Carrier
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search carriers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
          
          {carriersLoading ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Loading carriers...</p>
            </div>
          ) : filteredCarriers.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">
                    {searchTerm ? "No carriers found matching your search" : "No carriers found"}
                  </p>
                  {searchTerm ? (
                    <Button variant="outline" onClick={() => setSearchTerm("")}>
                      Clear search
                    </Button>
                  ) : (
                    <Button onClick={() => setShowCreateModal(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Carrier
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <button
                            onClick={cycleNameSort}
                            className="flex items-center gap-1 hover:text-gray-700 transition-colors"
                          >
                            Carrier Name
                            {nameSort === "asc" ? (
                              <ArrowUp className="w-3 h-3" />
                            ) : nameSort === "desc" ? (
                              <ArrowDown className="w-3 h-3" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3 opacity-40" />
                            )}
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          DOT Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Docket Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredCarriers.map((carrier: any) => (
                        <tr key={carrier.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback>
                                  <Truck className="w-5 h-5 text-gray-500" />
                                </AvatarFallback>
                              </Avatar>
                              <div className="ml-4">
                                <button 
                                  onClick={() => handleViewCarrier(carrier)}
                                  className="text-left hover:opacity-80 transition-opacity"
                                >
                                  <div className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline">{carrier.name}</div>
                                  <div className="text-sm text-gray-500">{carrier.email}</div>
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {carrier.company?.dotProfile?.dotNumber || carrier.company?.dotNumber || "Not set"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {carrier.docketNumber ? `${carrier.docketPrefix}-${carrier.docketNumber}` : "Not set"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {carrier.company?.id ? (
                              <button 
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                onClick={() => {
                                  setLocation(`/companies?id=${carrier.company.id}`);
                                }}
                              >
                                {carrier.company.name}
                              </button>
                            ) : (
                              <span className="text-gray-900">Unknown</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <NotesDialog
                              entityType="carrier"
                              entityId={carrier.id.toString()}
                              entityName={carrier.name}
                            />
                            {carrier.docketNumber && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleViewProfile(`${carrier.docketPrefix}${carrier.docketNumber}`)}
                                title="View FMCSA Profile"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleEditCarrier(carrier)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleViewCarrier(carrier)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
          
          <div className="flex flex-col items-center gap-2 mt-4 px-4 pb-4">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              Showing {filteredCarriers.length} of {carriersTotalCount}
            </p>
            {carriersHasNextPage && (
              <Button variant="outline" onClick={() => carriersFetchNextPage()} disabled={carriersIsFetchingNextPage}
                aria-label="Load more carriers" aria-busy={carriersIsFetchingNextPage}>
                {carriersIsFetchingNextPage ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                ) : "Load more"}
              </Button>
            )}
          </div>

          {showCreateModal && (
            <CreateCarrierModal
              onClose={() => setShowCreateModal(false)}
              onSubmit={handleCreateCarrier}
              isLoading={createCarrierMutation.isPending}
              companies={companies || []}
            />
          )}
          
          {showEditModal && selectedCarrier && (
            <EditCarrierModal
              open={showEditModal}
              onClose={() => {
                setShowEditModal(false);
                setSelectedCarrier(null);
              }}
              onSubmit={handleUpdateCarrier}
              isLoading={updateCarrierMutation.isPending}
              carrier={selectedCarrier}
              companies={companies || []}
            />
          )}
        </div>
    </ResponsiveLayout>
  );
}
